import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  inject,
  Renderer2,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AccessTypeE } from '@generated/be';
import { AppRoutes, hasAccess$ } from '@reza-ais-fe/reza-ais-fe-utils';
import { MenubarHoverOpenDirective, WrapperComponent } from '@shared/ui/components';
import { AuthService, UIService } from '@shared/utils';
import { cloneDeep, isEqual } from 'lodash-es';
import { MenuItem } from '@openng/optimus-ui/api';
import { Menubar, MenubarModule } from '@openng/optimus-ui/menubar';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { MOBILE_NAV_BREAKPOINT, MobileNavService } from '../../services/mobile-nav.service';
import { NavigationService } from '../../services/navigation.service';
import { navigationItems } from './app-navbav.constants';

@Component({
  selector: 'reza-navbar',
  templateUrl: './navbar.component.html',
  // bílý pruh navigace podle gov.cz (vzor AISK); relative: mobilní seznam menubaru se rozbalí přes celou šířku pruhu.
  // Vzhled položek je varianta .app-nav-bar v aplikaci (styles/reza-components.css, oddíl 7).
  host: { class: 'block relative bg-(--component-nav-background)' },
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [WrapperComponent, MenubarModule, MenubarHoverOpenDirective, CommonModule],
})
export class NavbarComponent {
  @HostBinding('class.is-test')
  get isTestEnvironmentClass() {
    return this._ui.isTestEnv;
  }

  private readonly authService = inject(AuthService);
  private readonly _ui = inject(UIService);
  private readonly navigationService = inject(NavigationService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly menubar = viewChild(Menubar);
  /** Odpojení posluchače kliknutí na document; existuje jen, dokud je mobilní menu otevřené. */
  private removeOutsideClickListener?: () => void;

  /** Pod touto šířkou je navigace v mobilním režimu – seznam rozbaluje tlačítko menu v záhlaví. */
  readonly mobileBreakpoint = MOBILE_NAV_BREAKPOINT;

  // Položky podle přístupů uživatele, aktivní položka podle aktuální URL (třída `is-route-active`)
  readonly navigation$ = this.navigationService.withActiveState(this.getNavigationItems$());

  constructor() {
    // Tlačítko menu v záhlaví rozbalí / sbalí mobilní seznam navigace
    inject(MobileNavService)
      .toggle$.pipe(takeUntilDestroyed())
      .subscribe((event) => {
        const menubar = this.menubar();
        if (!menubar) {
          return;
        }
        // Připojit už před toggle(): menubar si v toggle() připojí vlastní posluchač na document, který menu
        // zavře jen přepnutím příznaku (bez hide() a překreslení) – náš proto musí běžet dřív.
        if (!menubar.mobileActive) {
          this.setOutsideClickListener(true);
        }
        menubar.toggle(event);
        if (!menubar.mobileActive) {
          this.setOutsideClickListener(false);
        }
      });
    inject(DestroyRef).onDestroy(() => this.setOutsideClickListener(false));
  }

  /**
   * Klik mimo navigaci zavře mobilní menu. Posluchač na document je připojený jen při otevřeném menu,
   * aby ostatní kliky v zoneless aplikaci zbytečně neplánovaly detekci změn.
   */
  private setOutsideClickListener(menuOpen: boolean): void {
    if (menuOpen) {
      this.removeOutsideClickListener ??= this.renderer.listen(this.document, 'click', (event: MouseEvent) =>
        this.onDocumentClick(event)
      );
    } else {
      this.removeOutsideClickListener?.();
      this.removeOutsideClickListener = undefined;
    }
  }

  private onDocumentClick(event: MouseEvent): void {
    const menubar = this.menubar();
    if (menubar?.mobileActive && !this.host.nativeElement.contains(event.target as Node)) {
      // vlastní událost: toggle() volá preventDefault() a skutečnému kliknutí (např. na odkaz) nesmí zabránit
      menubar.toggle(new MouseEvent('click'));
    }
    // menu je zavřené (tímto klikem, nebo ho zavřel sám menubar – klik na položku, změna šířky okna)
    if (!menubar?.mobileActive) {
      this.setOutsideClickListener(false);
      this.cdr.markForCheck();
    }
  }

  private getNavigationItems$(): Observable<MenuItem[]> {
    return combineLatest([
      this.authService.user$,
      hasAccess$(AccessTypeE.Opravneni),
      hasAccess$(AccessTypeE.Sablony, AccessTypeE.SablonyVytvareni),
      hasAccess$(AccessTypeE.SablonySkupiny, AccessTypeE.SablonySkupinyVytvareni),
      hasAccess$(AccessTypeE.Certifikaty),
      hasAccess$(AccessTypeE.CertifikatyOvereni),
      hasAccess$(AccessTypeE.Ciselniky, AccessTypeE.CiselnikyEditace),
      hasAccess$(AccessTypeE.OpravneniOvereni),
      hasAccess$(AccessTypeE.StatistikyOkz),
      hasAccess$(AccessTypeE.StatistikySablony),
      hasAccess$(AccessTypeE.StatistikyCertifikaty),
      hasAccess$(AccessTypeE.Reklamace, AccessTypeE.ReklamaceSpravce),
      hasAccess$(AccessTypeE.ReklamaceImpl),
      hasAccess$(AccessTypeE.ArchivaceOkz),
      hasAccess$(AccessTypeE.ArchivaceSablony),
      hasAccess$(AccessTypeE.ArchivaceCertifikaty),
    ]).pipe(
      // filter(([user]) => !!user), // Ensure user is logged in - commented out because navbar is now available in public too
      distinctUntilChanged(isEqual),
      map(
        ([
          user,
          hasOpravneniAccess,
          hasSablonyAccess,
          hasSablonySkupinyAccess,
          hasCertifikatAccess,
          hasCertifikatOvereniAccess,
          hasCiselnikyAccess,
          hasOpravneniOvereniAccess,
          hasStatistikyOkzAccess,
          hasStatistikySablonyAccess,
          hasStatistikyCertifikatyAccess,
          hasReklamaceAccess,
          hasReklamaceImplAccess,
          hasArchivaceOkzAccess,
          hasArchivaceSablonyAccess,
          hasArchivaceCertifikatyAccess,
        ]) =>
          this.filterNavigationItems(
            user,
            hasOpravneniAccess,
            hasSablonyAccess,
            hasSablonySkupinyAccess,
            hasCertifikatAccess,
            hasCertifikatOvereniAccess,
            hasCiselnikyAccess,
            hasOpravneniOvereniAccess,
            hasStatistikyOkzAccess,
            hasStatistikySablonyAccess,
            hasStatistikyCertifikatyAccess,
            hasReklamaceAccess,
            hasReklamaceImplAccess,
            hasArchivaceOkzAccess,
            hasArchivaceSablonyAccess,
            hasArchivaceCertifikatyAccess
          )
      )
    );
  }

  private filterNavigationItems(
    user: boolean,
    hasOpravneniAccess: boolean,
    hasSablonyAccess: boolean,
    hasSablonySkupinyAccess: boolean,
    hasCertifikatAccess: boolean,
    hasCertifikatOvereniAccess: boolean,
    hasCiselnikyAccess: boolean,
    hasOpravneniOvereniAccess: boolean,
    hasStatistikyOkzAccess: boolean,
    hasStatistikySablonyAccess: boolean,
    hasStatistikyCertifikatyAccess: boolean,
    hasReklamaceAccess: boolean,
    hasReklamaceImplAccess: boolean,
    hasArchivaceOkzAccess: boolean,
    hasArchivaceSablonyAccess: boolean,
    hasArchivaceCertifikatyAccess: boolean
  ): MenuItem[] {
    const clonedNavigationItems: MenuItem[] = cloneDeep(navigationItems);

    if (!user) {
      return [];
    }

    const rtn = clonedNavigationItems
      .map((item) => {
        if (item.routerLink === AppRoutes.Opravneni && !hasOpravneniAccess) {
          return null;
        }
        if (item.routerLink === AppRoutes.Certifikaty && !hasCertifikatAccess) {
          return null;
        }
        if (item.routerLink === AppRoutes.CertifikatyOvereni && !hasCertifikatOvereniAccess) {
          return null;
        }

        if (item.id === AppRoutes.OvereniOpravneni && !hasOpravneniOvereniAccess) {
          return null;
        }
        if (
          item.id === AppRoutes.Statistiky &&
          !hasStatistikyOkzAccess &&
          !hasStatistikySablonyAccess &&
          !hasStatistikyCertifikatyAccess
        ) {
          return null;
        }
        if (
          item.id === AppRoutes.Archiv &&
          !hasArchivaceOkzAccess &&
          !hasArchivaceSablonyAccess &&
          !hasArchivaceCertifikatyAccess
        ) {
          return null;
        }

        const newItem = { ...item };

        if (item.id === AppRoutes.Ciselniky) {
          const filteredSubItems = item.items?.filter((subItem) => {
            if (subItem.id === AppRoutes.ObjektyOmezeni && !hasCiselnikyAccess) return false;
            if (subItem.id === AppRoutes.SpecifickaOmezeni && !hasCiselnikyAccess) return false;
            if (subItem.id === AppRoutes.Role && !hasCiselnikyAccess) return false;
            return true;
          });

          if (!filteredSubItems || filteredSubItems.length === 0) return null;

          newItem.items = filteredSubItems;
        }

        if (item.id === AppRoutes.Reklamace) {
          const filteredSubItems = item.items?.filter((subItem) => {
            if (subItem.id === AppRoutes.Reklamace && !hasReklamaceAccess) return false;
            if (subItem.id === AppRoutes.Reklamace_ImplicitnichOpravneni && !hasReklamaceImplAccess) return false;
            if (subItem.id === AppRoutes.Reklamace_KontrolaReklamaciImplicitnichOpravneni && !hasReklamaceImplAccess)
              return false;
            return true;
          });

          if (!filteredSubItems || filteredSubItems.length === 0) return null;

          newItem.items = filteredSubItems;
        }

        if (item.id === AppRoutes.Sablony) {
          const filteredSubItems = item.items?.filter((subItem) => {
            if (subItem.id === AppRoutes.Sablony && !hasSablonyAccess) return false;
            if (subItem.id === AppRoutes.Sablony_Skupiny && !hasSablonySkupinyAccess) return false;
            return true;
          });

          if (!filteredSubItems || filteredSubItems.length === 0) return null;

          newItem.items = filteredSubItems;
        }

        if (
          item.id === AppRoutes.Statistiky &&
          (hasStatistikyCertifikatyAccess || hasStatistikySablonyAccess || hasStatistikyOkzAccess)
        ) {
          const filteredSubItems = item.items?.filter((subItem) => {
            if (subItem.id === AppRoutes.Statistiky_PocetOpravneniKZastupovani && !hasStatistikyOkzAccess) {
              return false;
            }
            if (subItem.id === AppRoutes.Statistiky_ZmenyOpravneniKZastupovani && !hasStatistikyOkzAccess) {
              return false;
            }
            if (subItem.id === AppRoutes.Statistiky_OpravneniKZastupovaniDleSablon && !hasStatistikySablonyAccess) {
              return false;
            }
            if (subItem.id === AppRoutes.Statistiky_PoctuCertifikatu && !hasStatistikyCertifikatyAccess) {
              return false;
            }
            return true;
          });

          if (!filteredSubItems || filteredSubItems.length === 0) {
            return null;
          }

          newItem.items = filteredSubItems;
        }

        if (
          item.id === AppRoutes.Archiv &&
          (hasArchivaceOkzAccess || hasArchivaceSablonyAccess || hasArchivaceCertifikatyAccess)
        ) {
          const filteredSubItems = item.items?.filter((subItem) => {
            if (subItem.id === AppRoutes.Opravneni && !hasArchivaceOkzAccess) {
              return false;
            }
            if (subItem.id === AppRoutes.Sablony && !hasArchivaceSablonyAccess) {
              return false;
            }
            if (subItem.id === AppRoutes.Certifikaty && !hasArchivaceCertifikatyAccess) {
              return false;
            }
            return true;
          });

          if (!filteredSubItems || filteredSubItems.length === 0) {
            return null;
          }

          newItem.items = filteredSubItems;
        }

        return newItem;
      })
      .filter((item) => item !== null) as MenuItem[];
    return rtn;
  }
}
